import React, { useState, useEffect } from 'react';
import {
  ActivitySquare,
  Ticket,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Calendar,
  Users,
  MapPin,
  Camera,
  FileSpreadsheet,
  Mail,
  ArrowRight,
  RefreshCw,
  Search,
  Filter,
  CheckCheck,
  Hammer,
  Zap,
  Phone,
  Layers,
  Sparkles,
  Smartphone,
} from 'lucide-react';
import { TicketItem, TemporaryCaseItem, WorkOrderItem, StaffItem } from '../types/index.ts';

interface Here4uCenterPageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const Here4uCenterPage: React.FC<Here4uCenterPageProps> = ({ onNavigate }) => {
  const [pipelineData, setPipelineData] = useState<{
    totalBranches: number;
    totalTickets: number;
    pendingTemporaryCases: number;
    activeWorkOrders: number;
    staffOnSite: number;
    verifiedJobs: number;
  }>({
    totalBranches: 5,
    totalTickets: 3,
    pendingTemporaryCases: 1,
    activeWorkOrders: 1,
    staffOnSite: 1,
    verifiedJobs: 1,
  });

  const [tickets, setTickets] = useState<TicketItem[]>([]);
  const [tempCases, setTempCases] = useState<TemporaryCaseItem[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrderItem[]>([]);
  const [staffList, setStaffList] = useState<StaffItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterWorkflow, setFilterWorkflow] = useState<'ALL' | 'NORMAL' | 'MINOR_URGENT'>('ALL');
  const [syncingGmail, setSyncingGmail] = useState(false);
  const [syncNotice, setSyncNotice] = useState<string | null>(null);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [pipeRes, tktRes, tmpRes, woRes, stfRes] = await Promise.all([
        fetch('/api/here4u/pipeline').then((r) => (r.ok ? r.json() : null)),
        fetch('/api/tickets').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/temporary-cases').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/work-orders').then((r) => (r.ok ? r.json() : [])),
        fetch('/api/staff').then((r) => (r.ok ? r.json() : [])),
      ]);

      if (pipeRes) setPipelineData(pipeRes);
      if (Array.isArray(tktRes)) setTickets(tktRes);
      if (Array.isArray(tmpRes)) setTempCases(tmpRes);
      if (Array.isArray(woRes)) setWorkOrders(woRes);
      if (Array.isArray(stfRes)) setStaffList(stfRes);
    } catch (err: any) {
      console.warn('HERE4U Center load notice:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleSyncGmail = async () => {
    try {
      setSyncingGmail(true);
      setSyncNotice(null);
      const res = await fetch('/api/gmail/sync', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ simulatedEmail: null }),
      });
      const data = await res.json();
      if (res.ok) {
        setSyncNotice(`Gmail synced: ${data.message || 'Latest emails checked and parsed'}`);
        await fetchDashboardData();
      } else {
        setSyncNotice(data.error || 'Failed to sync Gmail');
      }
    } catch (err: any) {
      setSyncNotice(err.message || 'Sync error');
    } finally {
      setSyncingGmail(false);
      setTimeout(() => setSyncNotice(null), 6000);
    }
  };

  // Pipeline funnel stages counts
  const newTicketsCount = tickets.filter((t) => t.status === 'NEW').length;
  const pendingTempCount = tempCases.filter((c) => c.status === 'PENDING_TICKET_NUMBER').length;
  const surveyPendingCount = tickets.filter((t) => t.status === 'SITE_SURVEY').length;
  const boqPendingCount = tickets.filter((t) => t.status === 'BOQ').length;
  const quotationPendingCount = tickets.filter((t) => t.status === 'QUOTATION').length;
  const approvalPendingCount = tickets.filter((t) => t.status === 'APPROVAL').length;
  const assignedCount = tickets.filter((t) => t.status === 'ASSIGNED').length;
  const inProgressCount = tickets.filter((t) => t.status === 'WORK_IN_PROGRESS').length;
  const verificationPendingCount = tickets.filter((t) => t.status === 'COMPLETED' || t.verificationStatus === 'PENDING').length;
  const closedCount = tickets.filter((t) => t.status === 'CLOSED').length;

  const funnelStages = [
    { title: 'New Tickets', count: newTicketsCount, route: 'tickets', color: 'border-blue-500 bg-blue-50/60 text-blue-900', step: '01' },
    { title: 'Temp Cases', count: pendingTempCount, route: 'temp-cases', color: 'border-amber-500 bg-amber-50/60 text-amber-900', step: '02' },
    { title: 'Survey Pending', count: surveyPendingCount, route: 'surveys', color: 'border-purple-500 bg-purple-50/60 text-purple-900', step: '03' },
    { title: 'BOQ Estimating', count: boqPendingCount, route: 'boqs', color: 'border-cyan-500 bg-cyan-50/60 text-cyan-900', step: '04' },
    { title: 'Quotation Pending', count: quotationPendingCount, route: 'quotations', color: 'border-indigo-500 bg-indigo-50/60 text-indigo-900', step: '05' },
    { title: 'Approval Pending', count: approvalPendingCount, route: 'quotations', color: 'border-yellow-500 bg-yellow-50/60 text-yellow-900', step: '06' },
    { title: 'Staff Assigned', count: assignedCount, route: 'work-orders', color: 'border-teal-500 bg-teal-50/60 text-teal-900', step: '07' },
    { title: 'Work In Progress', count: inProgressCount, route: 'sitework', color: 'border-orange-500 bg-orange-50/60 text-orange-900', step: '08' },
    { title: 'Verification Pending', count: verificationPendingCount, route: 'verification', color: 'border-rose-500 bg-rose-50/60 text-rose-900', step: '09' },
    { title: 'Job Closed', count: closedCount, route: 'tickets', color: 'border-emerald-500 bg-emerald-50/60 text-emerald-900', step: '10' },
  ];

  // Minor urgent fast-track list
  const urgentOrders = workOrders.filter((wo) => wo.workflowType === 'MINOR_URGENT' || wo.status === 'ASSIGNED');

  // Filtered active tickets
  const filteredTickets = tickets.filter((t) => {
    const matchesSearch =
      t.ticketNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (t.branchName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.subject.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-2 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              HERE4U Operations Command Center
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-300">
              Live Facility Ops
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            End-to-end management for UBL maintenance requests, automated email extraction, branch coordination, and field verification.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <button
            onClick={() => onNavigate('mobile-job')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold rounded-lg bg-amber-500 text-slate-950 hover:bg-amber-400 shadow-sm transition-colors"
          >
            <Smartphone className="w-3.5 h-3.5 text-slate-950" />
            Field Mobile App
          </button>

          <button
            onClick={handleSyncGmail}
            disabled={syncingGmail}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 text-blue-600 ${syncingGmail ? 'animate-spin' : ''}`} />
            {syncingGmail ? 'Checking Mailbox...' : 'Sync Gmail'}
          </button>

          <button
            onClick={() => onNavigate('estimator-templates')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm transition-colors"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" />
            Estimator (9 Types)
          </button>

          <button
            onClick={() => onNavigate('work-orders')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800 shadow-sm transition-colors"
          >
            <Hammer className="w-3.5 h-3.5 text-blue-200" />
            New Work Order
          </button>
        </div>
      </div>

      {syncNotice && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs font-medium text-blue-900 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-blue-600 shrink-0" />
            <span>{syncNotice}</span>
          </div>
          <button
            onClick={() => onNavigate('gmail')}
            className="text-xs font-bold text-blue-700 hover:underline"
          >
            View in Gmail Center &rarr;
          </button>
        </div>
      )}

      {/* Operational Highlights Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Total Tickets</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{pipelineData.totalTickets}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">Active across UBL</div>
        </div>

        <div className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Pending Temp Cases</div>
          <div className="text-xl font-bold text-amber-600 mt-1">{pipelineData.pendingTemporaryCases}</div>
          <div className="text-[11px] text-amber-700 mt-0.5">Awaiting TKT #</div>
        </div>

        <div className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Active Work Orders</div>
          <div className="text-xl font-bold text-blue-600 mt-1">{pipelineData.activeWorkOrders}</div>
          <div className="text-[11px] text-blue-700 mt-0.5">In field execution</div>
        </div>

        <div className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Staff On Site</div>
          <div className="text-xl font-bold text-emerald-600 mt-1">{pipelineData.staffOnSite}</div>
          <div className="text-[11px] text-emerald-700 mt-0.5">GPS attendance active</div>
        </div>

        <div className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Verified Jobs</div>
          <div className="text-xl font-bold text-indigo-600 mt-1">{pipelineData.verifiedJobs}</div>
          <div className="text-[11px] text-indigo-700 mt-0.5">Joint signoff done</div>
        </div>

        <div className="p-3.5 bg-white border border-slate-200 rounded-xl shadow-xs">
          <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wider">Covered Branches</div>
          <div className="text-xl font-bold text-slate-900 mt-1">{pipelineData.totalBranches}</div>
          <div className="text-[11px] text-slate-400 mt-0.5">South & Central Hub</div>
        </div>
      </div>

      {/* 10-Stage Pipeline Funnel Cards */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
            <Layers className="w-4 h-4 text-blue-600" />
            HERE4U End-to-End Operational Pipeline (10 Stages)
          </h2>
          <span className="text-xs text-slate-500">Click any stage to view matching tasks</span>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5">
          {funnelStages.map((stage) => (
            <button
              key={stage.step}
              onClick={() => onNavigate(stage.route)}
              className={`p-3 rounded-lg border text-left transition-all hover:shadow-md cursor-pointer ${stage.color}`}
            >
              <div className="flex items-center justify-between text-[11px] font-mono text-slate-500">
                <span>STAGE {stage.step}</span>
                <ArrowRight className="w-3 h-3 text-slate-400" />
              </div>
              <div className="text-lg font-black mt-1">{stage.count}</div>
              <div className="text-xs font-semibold leading-tight mt-0.5 truncate">{stage.title}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Two-Column Middle Section: Fast-Track Urgent + Live Field Force */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Fast-Track Minor/Urgent Panel */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5 shadow-xs">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-bold text-slate-900">
                Fast-Track Minor / Urgent Dispatches
              </h3>
            </div>
            <span className="text-[11px] text-slate-500">Rapid Response SLA: &lt; 24h</span>
          </div>

          <div className="divide-y divide-slate-100 mt-2">
            {urgentOrders.length === 0 ? (
              <div className="py-6 text-center text-xs text-slate-400">
                No active critical urgent dispatches right now.
              </div>
            ) : (
              urgentOrders.map((wo) => (
                <div key={wo.id} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-slate-900">{wo.workOrderNumber}</span>
                      <span className="text-xs text-slate-500">· {wo.branchName}</span>
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200">
                        URGENT
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 line-clamp-1">{wo.scope}</p>
                    <div className="flex items-center gap-3 text-[11px] text-slate-400">
                      <span>Supervisor: {wo.supervisorName}</span>
                      <span>·</span>
                      <span>Team: {wo.teamName}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      onClick={() => onNavigate('attendance')}
                      className="px-2.5 py-1.5 text-xs font-medium rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                    >
                      <MapPin className="w-3.5 h-3.5 inline mr-1 text-slate-500" />
                      GPS
                    </button>
                    <button
                      onClick={() => onNavigate('work-orders')}
                      className="px-2.5 py-1.5 text-xs font-semibold rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                    >
                      Open Order
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Live Field Force Summary */}
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-bold text-slate-900">Today's Field Force</h3>
            </div>
            <button
              onClick={() => onNavigate('staff')}
              className="text-xs text-blue-600 font-semibold hover:underline"
            >
              Manage &rarr;
            </button>
          </div>

          <div className="space-y-3">
            {staffList.slice(0, 4).map((member) => (
              <div key={member.id} className="flex items-center justify-between text-xs">
                <div className="space-y-0.5">
                  <div className="font-semibold text-slate-900">{member.name}</div>
                  <div className="text-[11px] text-slate-500">{member.designation} · {member.teamName}</div>
                </div>
                <div className="text-right">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${
                      member.availability === 'ON_SITE'
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                        : member.availability === 'AVAILABLE'
                        ? 'bg-blue-50 text-blue-700 border border-blue-200'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {member.availability.replace('_', ' ')}
                  </span>
                  <div className="text-[10px] text-slate-400 mt-0.5">{member.mobile}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
            <span className="text-slate-500">Need to record attendance?</span>
            <button
              onClick={() => onNavigate('attendance')}
              className="text-xs font-bold text-blue-700 hover:underline inline-flex items-center gap-1"
            >
              <MapPin className="w-3.5 h-3.5" />
              Check In
            </button>
          </div>
        </div>
      </div>

      {/* Operational Ticket Registry & Actions */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100">
          <div>
            <h3 className="text-sm font-bold text-slate-900">Active HERE4U Branch Tickets</h3>
            <p className="text-xs text-slate-500">Real-time status of branch maintenance work and estimation</p>
          </div>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search ticket, branch..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 w-48 sm:w-56"
              />
            </div>
            <button
              onClick={() => onNavigate('tickets')}
              className="text-xs font-semibold text-blue-600 hover:underline px-2"
            >
              View All ({tickets.length}) &rarr;
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50/70 text-slate-600 font-semibold">
                <th className="py-2.5 px-3">Ticket #</th>
                <th className="py-2.5 px-3">Branch</th>
                <th className="py-2.5 px-3">Subject / Work Type</th>
                <th className="py-2.5 px-3">Status</th>
                <th className="py-2.5 px-3">Verification</th>
                <th className="py-2.5 px-3">Assigned Lead</th>
                <th className="py-2.5 px-3 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-700">
              {filteredTickets.map((tkt) => (
                <tr key={tkt.id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="py-2.5 px-3 font-mono font-bold text-slate-900">{tkt.ticketNumber}</td>
                  <td className="py-2.5 px-3">
                    <div className="font-medium text-slate-900">{tkt.branchName}</div>
                    <div className="text-[11px] text-slate-400">{tkt.branchCode}</div>
                  </td>
                  <td className="py-2.5 px-3">
                    <div className="font-medium text-slate-800 line-clamp-1">{tkt.subject}</div>
                    <div className="text-[11px] text-slate-500">{tkt.category}</div>
                  </td>
                  <td className="py-2.5 px-3">
                    <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-800 border border-blue-200">
                      {tkt.status}
                    </span>
                  </td>
                  <td className="py-2.5 px-3">
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold ${
                        tkt.verificationStatus === 'VERIFIED'
                          ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                          : 'bg-amber-50 text-amber-800 border border-amber-200'
                      }`}
                    >
                      {tkt.verificationStatus || 'PENDING'}
                    </span>
                  </td>
                  <td className="py-2.5 px-3 text-slate-600">{tkt.assignedTo || 'Unassigned'}</td>
                  <td className="py-2.5 px-3 text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      <button
                        onClick={() => onNavigate('work-orders')}
                        className="px-2 py-1 text-[11px] font-medium rounded bg-slate-100 hover:bg-slate-200 text-slate-700"
                      >
                        Work Order
                      </button>
                      <button
                        onClick={() => onNavigate('verification')}
                        className="px-2 py-1 text-[11px] font-semibold rounded bg-blue-50 hover:bg-blue-100 text-blue-700"
                      >
                        Verify
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
